import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';

// Interface for user information
interface UserInfo {
  id: string;
  user?: string;
  email?: string;
  sessionId?: string;
}

// Interface for Datadog InternalContext
interface InternalContext {
  application_id: string;
  session_id: string | undefined;
  view?: {
    id: string;
    url: string;
    referrer: string;
    name?: string;
  };
  user_action?: {
    id: string | string[];
  };
}

// Extended RUM type
interface DatadogRumExtended {
  getInternalContext?: () => InternalContext;
  [key: string]: any;
}

let sessionReplayTimeout: number | null = null;
let hasStartedSessionReplay = false;

// Initialize the Datadog RUM SDK
export const initDatadog = (userInfo?: UserInfo): void => {
  const releaseTag = process.env.REACT_APP_DATADOG_RELEASE_TAG;
  if (!releaseTag) {
    console.warn('Datadog RUM initialization skipped: missing release tag');
    return;
  }
  // Only initialize if we have application ID and client token
  const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    console.warn('Datadog RUM initialization skipped: missing application ID or client token');
    return;
  }

  try {

    const config: RumInitConfiguration = {
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      service: 'ai-search',
      env: process.env.REACT_APP_BUILD_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0',
      allowedTracingUrls:[
        "https://ava.ahvint.com/",
        "https://ava-beta.ahvint.com/"
      ],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
    };

    datadogRum.init(config);
    datadogRum.setGlobalContextProperty('release_tag', releaseTag);

    // Set user information if available
    if (userInfo) {
      const userData: Record<string, any> = {
        id: userInfo.id,
      };

      if (userInfo.email) userData.email = userInfo.email;
      if (userInfo.user) userData.user = userInfo.user;

      // Apply the session ID if provided
      if (userInfo.sessionId) {
        // Set the session ID in the RUM context
        datadogRum.setGlobalContextProperty('session_id', userInfo.sessionId);
      }

      datadogRum.setUser({ id: userInfo.id, email: userInfo.email, user: userInfo.user, name: userInfo.user });
      console.log('Datadog RUM user set with for', userInfo.user);
    }

    // Enable session replay

    scheduleSessionReplayStart();
    console.log('Datadog RUM initialized');

  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
};

// Export a function to manually start recording
const startSessionReplay = () => {
  if (hasStartedSessionReplay) {
    return;
  }
  datadogRum.startSessionReplayRecording();
  hasStartedSessionReplay = true;
};

const scheduleSessionReplayStart = () => {
  if (hasStartedSessionReplay) {
    return;
  }

  if (sessionReplayTimeout !== null) {
    return;
  }

  if (typeof window === 'undefined') {
    startSessionReplay();
    return;
  }

  sessionReplayTimeout = window.setTimeout(() => {
    sessionReplayTimeout = null;
    startSessionReplay();
  }, 300);
};

// Export a function to manually stop recording
export const stopSessionRecording = (): void => {
  datadogRum.stopSessionReplayRecording();
};

// Export a function to set user information
export const setDatadogUser = (userInfo: UserInfo): void => {
  try {
    const userData: Record<string, any> = {
      id: userInfo.id,
    };

    if (userInfo.user) userData.user = userInfo.user;

    // Apply the session ID if provided
    if (userInfo.sessionId) {
      // Set the session ID in the RUM context
      datadogRum.setGlobalContextProperty('session_id', userInfo.sessionId);
    }

    datadogRum.setUser({ id: userInfo.id, email: userInfo.email, user: userInfo.user, name: userInfo.user });
  } catch (error) {
    console.error('Failed to set Datadog user information:', error);
  }
};

// Testing functions
export const isDatadogInitialized = (): boolean => {
  // Check if Datadog RUM is initialized by checking if internal context exists
  const ddRum = datadogRum as DatadogRumExtended;
  return !!ddRum.getInternalContext && !!ddRum.getInternalContext();
};

export const logTestEvent = (eventName: string, properties?: Record<string, any>): void => {
  try {
    datadogRum.addAction(eventName, properties || {});
    console.log(`Test event "${eventName}" logged to Datadog RUM`);
  } catch (error) {
    console.error(`Failed to log test event "${eventName}" to Datadog RUM:`, error);
  }
};

export const getCurrentSessionId = (): string | null => {
  try {
    const ddRum = datadogRum as DatadogRumExtended;
    if (ddRum.getInternalContext) {
      const context = ddRum.getInternalContext();
      return context?.session_id || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get current Datadog session ID:', error);
    return null;
  }
};
